// hooks/useTournamentPermissions.js
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

/**
 * Hook to manage tournament permissions for the current user
 * Determines what actions a user can perform on a specific tournament
 * 
 * @param {Object} tournament - Tournament object with id and other properties
 * @returns {Object} Permission checks and owner management functions
 */
export const useTournamentPermissions = (tournament) => {
  const { user } = useAuth();
  const [tournamentOwners, setTournamentOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all owners for the current tournament
   */
  const fetchTournamentOwners = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);


      const { data, error: fetchError } = await supabase
        .from('tournament_owners')
        .select(`
          id,
          user_id,
          role,
          assigned_by,
          assigned_at,
          users:user_id (
            id,
            username,
            display_name,
            avatar_url
          )
        `)
        .eq('tournament_id', tournament?.id)
        .order('assigned_at', { ascending: true });

      if (fetchError) throw fetchError;
      setTournamentOwners(data || []);
    } catch (err) {
      console.error('Error fetching tournament owners:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tournament?.id]);

  // Fetch tournament owners when tournament or user changes
  useEffect(() => {
    if (tournament?.id && user?.id) {
      fetchTournamentOwners();
    }
  }, [tournament?.id, user?.id, fetchTournamentOwners]);

  /**
   * Add a new tournament owner
   */
  const addTournamentOwner = async (userId, role = 'owner') => {
    try {
      setError(null);

      const { data, error: insertError } = await supabase
        .from('tournament_owners')
        .insert({
          tournament_id: tournament.id,
          user_id: userId,
          role,
          assigned_by: user.id
        })
        .select(`
          id,
          user_id,
          role,
          assigned_by,
          assigned_at,
          users:user_id (
            id,
            username,
            display_name,
            avatar_url
          )
        `)
        .single();

      if (insertError) throw insertError;

      // Update local state
      setTournamentOwners(prev => [...prev, data]);
      
      return { success: true, data };
    } catch (err) {
      console.error('Error adding tournament owner:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  /**
   * Remove a tournament owner
   */
  const removeTournamentOwner = async (ownerId) => {
    try {
      setError(null);

      const { error: deleteError } = await supabase
        .from('tournament_owners')
        .delete()
        .eq('id', ownerId);

      if (deleteError) throw deleteError;

      // Update local state
      setTournamentOwners(prev => prev.filter(owner => owner.id !== ownerId));
      
      return { success: true };
    } catch (err) {
      console.error('Error removing tournament owner:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  /**
   * Update tournament owner role
   */
  const updateTournamentOwnerRole = async (ownerId, newRole) => {
    try {
      setError(null);

      const { data, error: updateError } = await supabase
        .from('tournament_owners')
        .update({ role: newRole })
        .eq('id', ownerId)
        .select(`
          id,
          user_id,
          role,
          assigned_by,
          assigned_at,
          users:user_id (
            id,
            username,
            display_name,
            avatar_url
          )
        `)
        .single();

      if (updateError) throw updateError;

      // Update local state
      setTournamentOwners(prev => 
        prev.map(owner => owner.id === ownerId ? data : owner)
      );
      
      return { success: true, data };
    } catch (err) {
      console.error('Error updating tournament owner role:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  };

  // Memoized permission calculations
  const permissions = useMemo(() => {
    if (!user || !tournament) {
      return {
        canView: true, // Public tournaments can be viewed
        canManage: false,
        canEdit: false,
        canStart: false,
        canEditMatches: false,
        canDelete: false,
        canManageOwners: false,
        canSubmitResults: false,
        canApproveSubmissions: false,
        canViewSubmissions: false,
        isOwner: false,
        isAdmin: false
      };
    }

    const isGlobalAdmin = user.isAdmin;
    const isCreator = tournament.created_by === user.id;
    const isOwner = tournamentOwners.some(owner => owner.user_id === user.id);
    const canManageAsOwner = isCreator || isOwner || isGlobalAdmin;
    
    // Player submission permissions - any authenticated user can submit
    // (actual team membership will be verified in the service layer)
    const canSubmitResults = user && ['open', 'in_progress', 'running', 'playoffs', 'knockout', 'group_stage'].includes(tournament.status);
    
    // Approval permissions - only tournament owners/creators/admins
    const canApproveSubmissions = canManageAsOwner;
    const canViewSubmissions = canManageAsOwner;

    return {
      // Basic permissions
      canView: true, // Everyone can view tournaments
      
      // Management permissions (owners + admins)
      canManage: canManageAsOwner,
      canEdit: canManageAsOwner,
      canStart: canManageAsOwner,
      canEditMatches: canManageAsOwner,
      canManageOwners: canManageAsOwner,
      
      // Tournament submission permissions
      canSubmitResults,
      canApproveSubmissions,
      canViewSubmissions,
      
      // Delete permission (only global admins)
      canDelete: isGlobalAdmin,
      
      // Status flags
      isOwner,
      isAdmin: isGlobalAdmin,
      isCreator,
      
      // Permission level for UI
      permissionLevel: isGlobalAdmin ? 'admin' : isOwner ? 'owner' : isCreator ? 'creator' : 'user'
    };
  }, [user, tournament, tournamentOwners]);

  // Helper function to check if user can perform specific action
  const canPerform = (action) => {
    return permissions[`can${action.charAt(0).toUpperCase() + action.slice(1)}`] || false;
  };

  // Get owner information for current user
  const currentUserOwnership = useMemo(() => {
    if (!user) return null;
    return tournamentOwners.find(owner => owner.user_id === user.id) || null;
  }, [user, tournamentOwners]);

  return {
    // Permission checks
    ...permissions,
    canPerform,
    
    // Owner data
    tournamentOwners,
    currentUserOwnership,
    
    // Owner management functions
    addTournamentOwner,
    removeTournamentOwner,
    updateTournamentOwnerRole,
    refreshOwners: fetchTournamentOwners,
    
    // State
    loading,
    error,
    
    // Helper methods
    hasOwners: tournamentOwners.length > 0,
    ownerCount: tournamentOwners.length,
    
    // Get owners by role (for future use)
    getOwnersByRole: (role) => tournamentOwners.filter(owner => owner.role === role)
  };
};

export default useTournamentPermissions;